// ============================================================
// DrawChangeModal - a blocking "I understand" explanation shown whenever a
// vial change moves the number of units the user draws (refill, or an
// auto-promoted backup) even though the DOSE is unchanged. Replaces the
// old fleeting toast: the user must acknowledge it, and dismissing it is
// what continues the flow. The reassurance is deliberately 5th-grade and
// carries no math.
// ============================================================

import React from 'react';
import { Modal, Button } from './Components';
import { formatUnits } from '../utils/calculations';

interface DrawChangeModalProps {
  isOpen: boolean;
  oldUnits: number;
  newUnits: number;
  /** Optional peptide/vial name for a more concrete headline. */
  peptideName?: string;
  /** Called on "I understand" or any dismissal - continue the flow here. */
  onConfirm: () => void;
}

export const DrawChangeModal: React.FC<DrawChangeModalProps> = ({
  isOpen,
  oldUnits,
  newUnits,
  peptideName,
  onConfirm,
}) => {
  const stronger = newUnits < oldUnits;
  return (
    <Modal isOpen={isOpen} onClose={onConfirm} title="Your draw is changing">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-accent)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-4)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--color-text-tertiary)', marginBottom: '6px' }}>
            New draw{peptideName ? ` for ${peptideName}` : ''}
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '30px', fontWeight: 800, color: 'var(--color-text-primary)', lineHeight: 1.1 }}>
            {formatUnits(newUnits)} units
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
            was {formatUnits(oldUnits)} units
          </div>
        </div>

        <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--color-text-primary)', lineHeight: 1.55, margin: 0 }}>
          You're still taking the exact same dose. This vial is just mixed a little {stronger ? 'stronger' : 'weaker'}, so you fill the
          syringe a little {stronger ? 'less' : 'more'}.
        </p>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.55, margin: 0 }}>
          Think of it like {stronger ? 'stronger' : 'weaker'} juice: you need {stronger ? 'less' : 'more'} of it to get the same flavor.
          We're updating your plan's summary to match.
        </p>

        <Button variant="primary" size="lg" fullWidth onClick={onConfirm}>
          I understand
        </Button>
      </div>
    </Modal>
  );
};
